import React, { useMemo } from 'react';
import { AutoComplete as AntAutoComplete } from 'antd';
import { Input } from 'components/input';
import { useGraph } from 'components/layouts/components/visualisation/wrapper';
import { useGetSearchData } from 'api/visualisation/use-get-search';
import { renderNodeProperties } from './options/node-property';
import { renderTypes } from './options/node-type';
import { renderEdgeTypes } from './options/edge-type';
import { renderEdgeProperties } from './options/edge-property';
import { useGetSelectedSearchData } from 'api/visualisation/use-get-selected-search';
import { initData } from 'components/layouts/components/visualisation/container/initial/nodes';
import { formattedData } from 'components/layouts/components/visualisation/helpers/format-node';
import { useGetData } from 'api/visualisation/use-get-data';
import { graphRender } from 'components/layouts/components/visualisation/helpers/utils';
import { useParams } from 'react-router-dom';
import { renderComments } from './options/comment';

type Props = React.FC<{
  search: string | undefined;
  setSearch: (value: string) => void;
  setIsEnterSearch: React.Dispatch<React.SetStateAction<string>>;
  isEnterSearch: string;
  collapsed?: number;
}>;

export const AutoComplete: Props = ({ search, setSearch, isEnterSearch, setIsEnterSearch, collapsed }) => {
  const { graph, setGraphInfo, startOpenNode } = useGraph();
  const { id } = useParams();

  useGetData(
    {
      enabled: !!isEnterSearch,
      onSuccess: ({ data }) => {
        graph.clear();
        initData(graph, formattedData(data.nodes, data.edges, data.relationsCounts));
        graphRender(graph);

        setGraphInfo({
          nodeCount: graph.getNodes().length,
        });
        setIsEnterSearch('');
      },
    },
    isEnterSearch
  );

  const { data } = useGetSearchData({ enabled: search ? search.trim()?.length > 2 : false }, search?.trim() ?? '');

  const { mutate } = useGetSelectedSearchData(id as string, {
    onSuccess: (data) => {
      const {
        data: { nodes, edges, relationsCounts },
      } = data ?? {};

      initData(graph, formattedData(nodes, edges, relationsCounts));
      graphRender(graph);

      setGraphInfo({
        nodeCount: graph.getNodes().length,
      });
    },
  });

  const onSelect = (value: string, item: { id: string; mode: string; value: string; node_id?: string }) => {
    if (item.mode === 'comment') {
      mutate({ id: item.node_id || '', action: 'node' });
      startOpenNode({ id: item.node_id, isOpened: true, autoCommentsIsOpen: true });
    } else mutate({ id: item.id, action: item.mode });
  };
  const nodeTypes = useMemo(
    () => data.nodeTypes?.map(({ id, label, color }) => renderTypes(id, label, color, search ?? '')),
    [data.nodeTypes, search]
  );

  const edgeTypes = useMemo(
    () => data.edgeTypes?.map((edge) => renderEdgeTypes(edge, search ?? '')),
    [data.edgeTypes, search]
  );
  const comments = useMemo(
    () =>
      data.comments?.map((comment, index) =>
        renderComments(comment.id, comment.node_id, comment.comments, search ?? '', index)
      ) ?? [],
    [data.comments, search]
  );

  const nodeProperties = useMemo(
    () => data.nodeProperties?.map((node) => renderNodeProperties(search ?? '', node)),
    [data.nodeProperties, search]
  );

  const edgeProperties = useMemo(
    () => data.edgeProperties?.map((edge) => renderEdgeProperties(edge, search ?? '')),
    [data.edgeProperties, search]
  );

  const options = edgeProperties?.concat(edgeTypes).concat(nodeProperties).concat(nodeTypes).concat(comments);

  return (
    <AntAutoComplete
      popupClassName="search-visualisation"
      popupMatchSelectWidth={400}
      style={{ width: 400 }}
      dropdownStyle={{
        left: collapsed ? 510 : 30,
        top: 228,
        backdropFilter: 'blur(7px)',
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.5) 100%)',
      }}
      onSelect={onSelect}
      options={options}
      value={search}
    >
      <Input
        onPressEnter={({ target }) => {
          const value = (target as HTMLInputElement).value;
          if (value.length > 2) {
            setIsEnterSearch(value);
          }
        }}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="search"
      />
    </AntAutoComplete>
  );
};
